import React, { useEffect } from 'react'
import parse from 'html-react-parser'
import Image from 'next/image'

import { Text } from '@wartek-id/text'
import { Button } from '@wartek-id/button-v1'

import Header from 'app/BuktiKaryaPage/components/Header'
import { DocCollectionSection } from '../blocks/DocCollectionSection'
import { DocUploader } from '../blocks/DocUploader'
import { useBCKSInvitationCollection } from './useBCKSInvitationCollection'
import useKSPSTrackEvent from '../hooks/useKSPSTrackEvent'
import SectionInfoGaruda from '../components/SectionInfoGaruda'

export const BCKSInvitationCollectionPage = () => {
  const { KSPSTrackEvent } = useKSPSTrackEvent()
  const {
    handleCopyUrlClick,
    isDocValid,
    handleDocUpload,
    isUploading,
    docList,
    currentDoc,
    currentUploadingDoc,
    handleMutateDocList,
    handleRemoveTempDoc,
    isReviewMode,
    handleSaveDraft,
    handleNextStep,
    isDocDraftValid,
  } = useBCKSInvitationCollection()

  useEffect(() => {
    KSPSTrackEvent({
      event_name: 'enter_file_collection_page',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (currentDoc.name) {
    return (
      <DocUploader
        setDoc={handleDocUpload}
        selectedDocMeta={currentDoc}
        docData={docList[currentDoc.name]}
        onDocUpload={handleMutateDocList}
        isReviewMode={isReviewMode}
        onBackClick={handleNextStep}
      />
    )
  }

  return (
    <div className="relative flex flex-col h-screen max-w-xl mx-auto">
      <Header title="Pengumpulan berkas" forceNavigateBack />

      <div className="bg-[#ECF5FE] p-4">
        <Text variant="body-sm">
          Catatan: Pastikan dokumen asli (<strong>bukan fotokopi</strong>),
          ukuran <strong>maks. 5 MB</strong>.
        </Text>
      </div>

      <div className="p-4">
        <section className="flex mb-4 p-4 border border-subdued rounded-lg md:hidden lg:hidden">
          <div className="w-36 mr-4">
            <Image
              layout="fixed"
              src="/images/img-typing-monitor.png"
              width={84}
              height={84}
              alt="Ilustrasi pengunggahan berkas"
            />
          </div>
          <div>
            <Text variant="body-sm" color="subdued">
              {parse(
                `Pengunggahan berkas dianjurkan melalui web <b>Ruang GTK</b>.`
              )}
            </Text>

            <Button
              className="mt-4"
              color="black"
              size="sm"
              onClick={() => {
                handleCopyUrlClick(window.location.origin)
              }}
            >
              Salin tautan
            </Button>
          </div>
        </section>

        <DocCollectionSection
          title="Hasil Penilaian Kinerja"
          subtitle="Unggah hasil penilaian kinerja yang terbaru."
          exampleFilePath="/bcks/invitation/guide/kinerja"
          filePlaceholder="Pilih dokumen"
          fileData={
            isUploading && currentUploadingDoc.name === 'kinerja'
              ? { name: 'Hasil Penilaian Kinerja', url: 'dummyURL' }
              : { ...docList.kinerja }
          }
          isLoading={isUploading && currentUploadingDoc.name === 'kinerja'}
          onDocDeleteClick={() => {
            handleRemoveTempDoc('kinerja')
          }}
          onClick={() =>
            handleDocUpload({
              docName: 'kinerja',
            })
          }
        />

        <DocCollectionSection
          title="Surat Keterangan Catatan Kepolisian (masa berlaku 6 bulan)"
          subtitle="Unggah SK perilaku baik dari Polres setempat dengan masa berlaku 6 bulan sejak tanggal diterbitkan."
          exampleFilePath="/bcks/invitation/guide/police"
          filePlaceholder="Pilih dokumen"
          fileData={
            isUploading && currentUploadingDoc.name === 'police'
              ? { name: 'SKCK', url: 'dummyURL' }
              : { ...docList.police }
          }
          isLoading={isUploading && currentUploadingDoc.name === 'police'}
          onDocDeleteClick={() => {
            handleRemoveTempDoc('police')
          }}
          onClick={() =>
            handleDocUpload({
              docName: 'police',
            })
          }
        />

        <DocCollectionSection
          title="Surat Keterangan Pengalaman Manajerial"
          subtitle="Unggah surat keterangan atau keputusan dari Kepala Sekolah bahwa Anda memiliki 2 tahun pengalaman manajerial di satuan pendidikan, organisasi pendidikan, dan/atau komunitas pendidikan."
          extraDesc="*Khusus PPPK lampirkan juga bukti pengalaman kerja di dalam 1 dokumen yang sama"
          exampleFilePath="/bcks/invitation/guide/managerial"
          filePlaceholder="Pilih dokumen"
          fileData={
            isUploading && currentUploadingDoc.name === 'managerial'
              ? { name: 'SK Manajerial', url: 'dummyURL' }
              : { ...docList.managerial }
          }
          isLoading={isUploading && currentUploadingDoc.name === 'managerial'}
          onDocDeleteClick={() => {
            handleRemoveTempDoc('managerial')
          }}
          onClick={() =>
            handleDocUpload({
              docName: 'managerial',
            })
          }
        />

        <DocCollectionSection
          title="Surat Keterangan Bebas Hukuman Disiplin"
          subtitle="Unggah surat dari Kepala Sekolah bahwa Anda tidak pernah dijatuhi hukuman kedisplinan (tidak perlu materai)."
          exampleFilePath="/bcks/invitation/guide/discipline"
          filePlaceholder="Pilih dokumen"
          fileData={
            isUploading && currentUploadingDoc.name === 'discipline'
              ? { name: 'SK Bebas Hukuman', url: 'dummyURL' }
              : { ...docList.discipline }
          }
          isLoading={isUploading && currentUploadingDoc.name === 'discipline'}
          onDocDeleteClick={() => {
            handleRemoveTempDoc('discipline')
          }}
          onClick={() =>
            handleDocUpload({
              docName: 'discipline',
            })
          }
        />

        <DocCollectionSection
          title="Pakta Integritas"
          subtitle="Unggah dokumen perjanjian yang ditandatangani."
          exampleFilePath="/bcks/invitation/guide/pakta_integritas"
          filePlaceholder="Pilih dokumen"
          fileData={
            isUploading && currentUploadingDoc.name === 'pakta_integritas'
              ? { name: 'Pakta Integritas', url: 'dummyURL' }
              : { ...docList.pakta_integritas }
          }
          isLoading={
            isUploading && currentUploadingDoc.name === 'pakta_integritas'
          }
          onDocDeleteClick={() => {
            handleRemoveTempDoc('pakta_integritas')
          }}
          onClick={() =>
            handleDocUpload({
              docName: 'pakta_integritas',
            })
          }
        />

        <SectionInfoGaruda className="mb-6" />

        <div className="flex flex-row items-center">
          <Button
            fullWidth
            className="mr-2"
            size="lg"
            color="white"
            disabled={!isDocDraftValid}
            onClick={() => {
              KSPSTrackEvent({
                event_name: 'click_file_collection_save_draft',
              })
              handleSaveDraft()
            }}
          >
            Simpan Draf
          </Button>

          <Button
            fullWidth
            className="ml-2"
            size="lg"
            color="black"
            disabled={!isDocValid}
            onClick={() => {
              KSPSTrackEvent({
                event_name: 'click_file_collection_next',
              })
              handleNextStep()
            }}
          >
            Lanjutkan
          </Button>
        </div>
      </div>
    </div>
  )
}
